import { atom, computed, type ReadableAtom } from 'nanostores';
import { ChangeQueue } from './change-queue';
import { SupabaseEdgeFunctionsStore } from './edge-functions';
import { SupabaseMigrationsStore } from './migrations';
import { fetchSupabaseIntegration } from '~/lib/.client/api/supabase';
import { waitForProjectImport } from '~/lib/projects';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer } from '~/lib/webcontainer';
import type { PotentialSupabaseMigrationProblem } from '~/types/chat';
import type { SupabaseIntegration } from '~/types/supabase';
import { hmrPropertyFactory } from '~/utils/hmr';

export interface BaseConfirmationData {
  id: string;
}

export interface MigrationData {
  filePath: string;
  shortSummary: string;
  longSummary: string;
}

export interface MigrationsData extends BaseConfirmationData {
  type: 'migration';
  migrations: MigrationData[];
}

export interface ConnectData extends BaseConfirmationData {
  type: 'connect-required';
}

export interface MigrationErrorData extends BaseConfirmationData {
  type: 'migration-error';
  changeId: string;
  problems: [PotentialSupabaseMigrationProblem];
}

export type SupabaseChangeData = MigrationsData | ConnectData | MigrationErrorData;

export type SupabaseStatus =
  | 'UNKNOWN'
  | 'CONFIGURED'
  | 'CONNECTING'
  | 'NOT_CONFIGURED'
  | 'NEED_CONFIRMATION'
  | 'REMOVED'
  | 'UNSUPPORTED'
  | 'CONNECTING_TO_EXISTING_DATABASE'
  | 'UNHEALTHY';

export type SupabaseProjectConfigurationSteps = 'initial' | 'create-new' | 'connect-existing' | 'confirm-creation';

const hmr = hmrPropertyFactory('SupabaseStore');

/**
 * Global store holding all the state related to Supabase.
 */
export class SupabaseStore {
  @hmr()
  requiresConfirmation = atom<'idle' | 'pending' | 'accepted' | 'declined'>('idle');

  @hmr()
  showProjectLimitReached = atom<boolean>(false);

  @hmr()
  showProjectNameAlreadyExists = atom<boolean>(false);

  @hmr()
  showSupabaseProjectConfiguration = atom<boolean>(false);

  @hmr()
  isConfigVisible = atom<boolean>(false);

  @hmr()
  supabaseProjectConfigurationStep = atom<SupabaseProjectConfigurationSteps>('initial');

  @hmr()
  supabaseProjectIntegration = atom<SupabaseIntegration | undefined>();

  @hmr()
  supabaseStatus = atom<SupabaseStatus>('UNKNOWN');

  isConfigured = computed(this.supabaseStatus, (status) => status === 'CONFIGURED');
  supabaseProject = computed(this.supabaseProjectIntegration, (integration) => integration?.project);
  connecting = computed(this.supabaseStatus, (status) => status === 'CONNECTING');

  @hmr()
  applyingMigrations = atom(false);

  supported!: ReadableAtom<boolean>;

  @hmr()
  #changesQueue = new ChangeQueue();

  // adding the @hmr() annotation on those would be incorrect because `this` changes
  #migrationsStore = new SupabaseMigrationsStore(this, this.#changesQueue);
  #edgefunctionsStore = new SupabaseEdgeFunctionsStore(this, this.#changesQueue);

  get hasMigrations() {
    return this.#changesQueue.hasMigrations;
  }

  get firstItemInConfirmationQueue() {
    return this.#changesQueue.firstItemInConfirmationQueue;
  }

  readonly promptDisabled = computed(
    [this.supabaseStatus, this.applyingMigrations, this.firstItemInConfirmationQueue],
    (status, applyingMigrations, firstItemInConfirmationQueue) => {
      /**
       * @todo Not sure if it's the best to disable the prompt when there's failed migrations
       * because the user won't be able to prompt. On the other hand, we want the user to fix the
       * migrations using the "Attempt fix" button becasue it will contain more specific information
       * for how to fix the migration. However, it means the user could potentially be blocked
       * from sending anything if they the LLM ends up in an infinite loop and can't figure out
       * how to fix the issue.
       */
      let isPendingItem = false;

      if (firstItemInConfirmationQueue != undefined) {
        if (firstItemInConfirmationQueue.type === 'migration') {
          isPendingItem = true;
        } else if (firstItemInConfirmationQueue.type === 'connect-required') {
          isPendingItem = false; // explicitly dont disable prompt when connecting to supabase for now
        }
      }

      return status !== 'UNSUPPORTED' && (applyingMigrations || isPendingItem);
    },
  );

  init() {
    this.supported = computed([workbenchStore.framework, this.supabaseStatus], (framework, status) => {
      return status !== 'UNSUPPORTED' && (framework === undefined || framework.name !== 'nextjs');
    });

    this.supabaseProjectIntegration.subscribe(async (integration) => {
      if (integration) {
        await writeDotEnvFile(integration);
      }
    });

    this.#migrationsStore.init();
    this.#edgefunctionsStore.init();

    let setupDone = false;

    const unsubscribe = projectStore.projectId.subscribe(async (projectId) => {
      if (projectId && !setupDone) {
        setupDone = true;

        try {
          const integration = await fetchSupabaseIntegration(projectId);

          if (integration && 'notFound' in integration) {
            /**
             * When this call returns 404, it means there is no active connection to a supabase
             * project.
             *
             * At this point, we need to verify if the project already has migration files
             * cause when it does, this is a project that has previously been connected to
             * supabase but we don't know which one. This is an unsupported use case and we
             * should warn the user about this.
             */
            await waitForProjectImport();

            if (this.supabaseStatus.get() === 'UNKNOWN') {
              this.supabaseStatus.set('NOT_CONFIGURED');
            }

            return;
          }

          if (integration && integration.project.status !== 'ACTIVE_HEALTHY') {
            this.supabaseStatus.set('UNHEALTHY');

            return;
          }

          if (integration) {
            this.supabaseProjectIntegration.set(integration);
            this.supabaseStatus.set('CONFIGURED');
          }
        } catch {
          if (this.supabaseStatus.get() === 'UNKNOWN') {
            this.supabaseStatus.set('NOT_CONFIGURED');
          }
        }

        queueMicrotask(() => unsubscribe());
      }
    });
  }

  getEdgeFunctionsLogs(functionSlug: string) {
    return this.#edgefunctionsStore.getEdgeFunctionsLogs(functionSlug);
  }

  deployEdgeFunctions(): Promise<void> {
    return this.#edgefunctionsStore.deployEdgeFunctions();
  }

  getMigrationFilePath(filePath: string) {
    return this.#migrationsStore.getMigrationFilePath(filePath);
  }

  acceptChange(changeId: string) {
    return this.#migrationsStore.acceptChange(changeId);
  }

  discardChanges(changeId: string) {
    return this.#migrationsStore.discardChanges(changeId);
  }

  isDiscarded(changeId: string) {
    return this.#migrationsStore.isDiscarded(changeId);
  }

  showSupabaseCreateNewProjectConfiguration() {
    this.supabaseProjectConfigurationStep.set('create-new');
    this.showSupabaseProjectConfiguration.set(true);
  }
}

async function writeDotEnvFile(supbaseIntegration: SupabaseIntegration) {
  const webcontainerInstance = await webcontainer;

  await waitForProjectImport();

  let frameworkPrefix = 'VITE_';

  const framework = workbenchStore.framework.get()?.name;

  if (framework === 'nextjs') {
    frameworkPrefix = 'NEXT_PUBLIC_';
  }

  if (framework === 'expo') {
    frameworkPrefix = 'EXPO_PUBLIC_';
  }

  let dotEnv = '';

  try {
    dotEnv = await webcontainerInstance.fs.readFile('.env', 'utf-8');
  } catch {
    // noop
  }

  const lines = dotEnv.split('\n');

  const { apiKey, project } = supbaseIntegration;

  const apiKeyKey = `${frameworkPrefix}SUPABASE_ANON_KEY`;
  const databaseUrlKey = `${frameworkPrefix}SUPABASE_URL`;

  findAndReplaceKey(lines, apiKeyKey, apiKey);
  findAndReplaceKey(lines, databaseUrlKey, `https://${project.id}.supabase.co`);

  webcontainerInstance.fs.writeFile('.env', lines.join('\n'), 'utf8');
}

function findAndReplaceKey(lines: Array<string>, key: string, value: string) {
  const keyIndex = lines.findIndex((line) => line.trim().startsWith(`${key}=`));

  const newKeyValue = `${key}=${value}`;

  if (keyIndex !== -1) {
    lines.splice(keyIndex, 1, newKeyValue);
  } else {
    lines.push(newKeyValue);
  }
}

export const supabaseStore = new SupabaseStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { supabaseStore });
}
